import { TaskStatusBadge } from '@/components/badges/TaskStatusBadge';
import { formatDate } from '@/util/dates';
import { useRowClick } from '@/lib/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { tasksForHomeState, taskTypesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { type HomeId, type Task, TaskStatusSchema } from '@rock/types';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 50;

export const HomeTasksTab: FC<{ homeId: HomeId }> = ({ homeId }) => {
    // Status filter lives in the URL (?status=pending) per the tabs-driven-by-URL convention.
    const [searchParams, setSearchParams] = useSearchParams();
    const statusParam = searchParams.get('status');
    const status = TaskStatusSchema.options.find((option) => option === statusParam);
    const [limit, setLimit] = useState(PAGE_SIZE);

    const [tasks, tasksLoadable] = useLoadableAtomValue(
        useMemo(() => tasksForHomeState({ homeId, status, limit }), [homeId, status, limit]),
        [],
    );
    const [taskTypes] = useLoadableAtomValue(taskTypesState, []);
    const taskTypeTitleById = useMemo(() => {
        const map = new Map<string, string>();
        for (const tt of taskTypes) {
            map.set(tt.id, tt.title);
        }
        return map;
    }, [taskTypes]);

    const setStatus = (value: string) => {
        setLimit(PAGE_SIZE); // new filter, new page window
        setSearchParams(value === 'all' ? {} : { status: value }, { replace: true });
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <CardTitle>Tasks</CardTitle>
                        <CardDescription>
                            Tasks for this home, most recent due date first{status ? ` — showing ${status}` : ''}.
                        </CardDescription>
                    </div>
                    <Select value={status ?? 'all'} onValueChange={setStatus}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            {TaskStatusSchema.options.map((option) => (
                                <SelectItem key={option} value={option}>
                                    {option}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </CardHeader>
            <CardContent>
                {tasksLoadable.state === 'loading' ? (
                    <div className="section-description">Loading tasks…</div>
                ) : tasksLoadable.state === 'hasError' ? (
                    <div className="text-destructive text-sm">
                        Error loading tasks:{' '}
                        {tasksLoadable.error instanceof Error
                            ? tasksLoadable.error.message
                            : String(tasksLoadable.error)}
                    </div>
                ) : !tasks || tasks.length === 0 ? (
                    <div className="section-description">
                        {status ? `No ${status} tasks for this home.` : 'No tasks found for this home.'}
                    </div>
                ) : (
                    <>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Due Date</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Completed</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {tasks.map((task) => (
                                    <HomeTaskRow
                                        key={task.id}
                                        homeId={homeId}
                                        task={task}
                                        taskTypeTitleById={taskTypeTitleById}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                        {tasks.length >= limit && (
                            <div className="mt-4 flex justify-center">
                                <Button variant="outline" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                                    Load More
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

const HomeTaskRow: FC<{ homeId: HomeId; task: Task; taskTypeTitleById: Map<string, string> }> = ({
    homeId,
    task,
    taskTypeTitleById,
}) => {
    const onRowClick = useRowClick();
    return (
        <TableRow className="cursor-pointer" onClick={onRowClick(`/directory/homes/${homeId}/tasks/${task.id}`)}>
            <TableCell className="text-sm">{taskTypeTitleById.get(task.taskTypeId) ?? '-'}</TableCell>
            <TableCell className="text-sm">{formatDate(task.taskDate)}</TableCell>
            <TableCell>
                <TaskStatusBadge status={task.status} />
            </TableCell>
            <TableCell className="text-sm">{task.completedAt ? formatDate(task.completedAt) : '-'}</TableCell>
        </TableRow>
    );
};
